import * as THREE from 'three';
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { MeshoptDecoder } from 'three/addons/libs/meshopt_decoder.module.js';
import { RoundedBoxGeometry } from 'three/addons/geometries/RoundedBoxGeometry.js';
import { roundedBox, sphere } from './toon.js';
import { GAME_MODE_RULES, getCannon, getProjectile, STANDARD_CANNON_PERFORMANCE } from './data.js';
import {
  applyVehicleBodyFinish,
  prepareVehicleFinishMaterials,
} from '../tools/vehicleProfile.js';
import {
  convergeProjectileVisualOffset,
  projectileLaunchState,
  projectileVehicleIntersection,
} from './projectileCombat.js';
import { canFireProjectile, consumeProjectileFire } from './projectileFireLimiter.js';
import { DeferredClear } from './deferredClear.js';
import { steerHomingVelocity } from './powerups.js';

const cosmeticGlbLoader=new GLTFLoader();
const cosmeticDracoLoader=new DRACOLoader();
cosmeticDracoLoader.setDecoderPath(`${import.meta.env.BASE_URL}draco/`);
cosmeticGlbLoader.setDRACOLoader(cosmeticDracoLoader);
cosmeticGlbLoader.setMeshoptDecoder(MeshoptDecoder);
const cosmeticAssetPromises=new Map();
const nitroEmissionPosition=new THREE.Vector3();
const nitroEmissionDirection=new THREE.Vector3();
const nitroEmissionScale=new THREE.Vector3();
const nitroEmissionJitter=new THREE.Vector3();
const nitroEmissionDrift=new THREE.Vector3();
const nitroEmissionQuaternion=new THREE.Quaternion();

function materialBox(width,height,depth,material,radius=.03){
  return new THREE.Mesh(
    new RoundedBoxGeometry(
      width,
      height,
      depth,
      3,
      Math.min(radius,width*.25,height*.25,depth*.25),
    ),
    material,
  );
}

export function createCannonModel(design={},fallbackAccent='#ffcf43',paintFinish=null){
  const root=new THREE.Group();
  root.name=`cannon-${design.id||'custom'}`;
  root.userData.muzzleZ=-.79;
  root.userData.muzzleY=.03;

  if(design.assetUrl){
    const placeholder=roundedBox(.42,.28,.72,design.color||'#d9dde2',.08,4);
    placeholder.position.set(0,.18,-.3);
    root.add(placeholder);
    let promise=cosmeticAssetPromises.get(design.assetUrl);
    if(!promise){
      promise=cosmeticGlbLoader.loadAsync(design.assetUrl);
      cosmeticAssetPromises.set(design.assetUrl,promise);
    }
    promise.then((gltf)=>{
      const model=gltf.scene.clone(true);
      prepareVehicleFinishMaterials(model);
      if(paintFinish)applyVehicleBodyFinish(model,paintFinish);
      const orientedModel=new THREE.Group();
      orientedModel.rotation.set(
        THREE.MathUtils.degToRad(Number(design.modelRotationX)||0),
        THREE.MathUtils.degToRad(Number(design.modelRotationY)||0),
        THREE.MathUtils.degToRad(Number(design.modelRotationZ)||0),
      );
      orientedModel.add(model);
      const bounds=new THREE.Box3().setFromObject(orientedModel);
      const size=bounds.getSize(new THREE.Vector3());
      const center=bounds.getCenter(new THREE.Vector3());
      const modelScale=(
        1.15/(Math.max(size.x,size.y,size.z)||1)
      )*Math.max(.01,Number(design.modelScale)||1);
      orientedModel.scale.setScalar(modelScale);
      orientedModel.position.set(
        -center.x*modelScale,
        -bounds.min.y*modelScale,
        -bounds.max.z*modelScale,
      );
      model.traverse((child)=>{
        if(!child.isMesh)return;
        child.castShadow=true;
        child.receiveShadow=true;
      });
      root.clear();
      root.add(orientedModel);
      root.userData.muzzleZ=-size.z*modelScale;
      root.userData.muzzleY=Math.max(.03,size.y*modelScale*.5);
    }).catch(()=>{});
    return root;
  }

  const metalness=Number(design.metalness??.55);
  const roughness=Number(design.roughness??.32);
  const bodyMaterial=new THREE.MeshPhysicalMaterial({
    color:design.color||'#d9dde2',
    metalness,
    roughness,
    clearcoat:.4,
  });
  const accentMaterial=new THREE.MeshPhysicalMaterial({
    color:design.accent||fallbackAccent,
    metalness:Math.max(.2,metalness-.08),
    roughness:Math.min(.85,roughness+.02),
    clearcoat:.55,
  });
  const baseMaterial=new THREE.MeshStandardMaterial({
    color:design.baseColor||'#30303b',
    metalness:.42,
    roughness:.46,
  });
  const baseRadius=Number(design.baseRadius??.26);
  const baseTopRadius=Number(design.baseTopRadius??.2);
  const baseHeight=Number(design.baseHeight??.14);
  const barrelLength=Number(design.barrelLength??.72);
  const barrelRadius=Number(design.barrelRadius??.13);
  const muzzleLength=Number(design.muzzleLength??.09);
  const barrelY=baseHeight+.08;
  const base=new THREE.Mesh(
    new THREE.CylinderGeometry(baseTopRadius,baseRadius,baseHeight,24),
    baseMaterial,
  );
  base.position.y=baseHeight*.5;
  root.add(base);

  const addBarrel=(x)=>{
    const barrel=new THREE.Mesh(
      new THREE.CylinderGeometry(
        barrelRadius*Number(design.barrelTaper??.82),
        barrelRadius,
        barrelLength,
        20,
      ),
      bodyMaterial,
    );
    barrel.rotation.x=Math.PI/2;
    barrel.position.set(x,barrelY,-barrelLength*.44);
    const muzzle=new THREE.Mesh(
      new THREE.CylinderGeometry(
        barrelRadius*Number(design.muzzleScale??1.28),
        barrelRadius*Number(design.muzzleScale??1.28),
        muzzleLength,
        20,
      ),
      accentMaterial,
    );
    muzzle.rotation.x=Math.PI/2;
    muzzle.position.set(x,barrelY,-barrelLength*.91);
    root.add(barrel,muzzle);
  };

  if(design.style==='twin'){
    const separation=Number(design.twinSeparation??.23);
    addBarrel(-separation*.5);
    addBarrel(separation*.5);
    const bridge=materialBox(
      separation+barrelRadius*2.35,
      Number(design.bridgeHeight??.1),
      .18,
      accentMaterial,
      .035,
    );
    bridge.position.set(0,barrelY,-.12);
    root.add(bridge);
  }else if(design.style==='bubble'){
    const tankRadius=Number(design.tankRadius??.23);
    const tank=new THREE.Mesh(new THREE.SphereGeometry(tankRadius,24,16),accentMaterial);
    tank.scale.z=Number(design.tankStretch??1.12);
    tank.position.set(0,baseHeight+tankRadius*.72,-.06);
    root.add(tank);
    addBarrel(0);
  }else if(design.style==='rail'){
    const separation=Number(design.railSeparation??.18);
    const railWidth=Number(design.railWidth??.075);
    [-separation*.5,separation*.5].forEach((x)=>{
      const rail=materialBox(railWidth,railWidth,barrelLength,bodyMaterial,.025);
      rail.position.set(x,barrelY,-barrelLength*.43);
      root.add(rail);
    });
    const tip=materialBox(
      Number(design.tipWidth??.3),
      Number(design.tipHeight??.12),
      Number(design.tipLength??.13),
      accentMaterial,
      .04,
    );
    tip.position.set(0,barrelY,-barrelLength*.9);
    root.add(tip);
  }else{
    addBarrel(0);
  }
  root.userData.muzzleZ=-barrelLength*.91-muzzleLength*.5;
  root.userData.muzzleY=barrelY;
  root.traverse((child)=>{if(child.isMesh){child.castShadow=true;child.receiveShadow=true;}});
  return root;
}

function createTombstoneMarker(design,paintFinish=null) {
  const root = new THREE.Group();
  root.name = `tombstone-${design.id}`;
  if(design.assetUrl){
    const placeholder=roundedBox(.48,.62,.15,design.color,.13,4);
    placeholder.position.y=.31;
    root.add(placeholder);
    let promise=cosmeticAssetPromises.get(design.assetUrl);
    if(!promise){
      promise=cosmeticGlbLoader.loadAsync(design.assetUrl);
      cosmeticAssetPromises.set(design.assetUrl,promise);
    }
    promise.then((gltf)=>{
      const model=gltf.scene.clone(true);
      prepareVehicleFinishMaterials(model);
      if(paintFinish)applyVehicleBodyFinish(model,paintFinish);
      const orientedModel=new THREE.Group();
      orientedModel.rotation.set(
        THREE.MathUtils.degToRad(Number(design.modelRotationX)||0),
        THREE.MathUtils.degToRad(Number(design.modelRotationY)||0),
        THREE.MathUtils.degToRad(Number(design.modelRotationZ)||0),
      );
      orientedModel.add(model);
      const box=new THREE.Box3().setFromObject(orientedModel);
      const size=box.getSize(new THREE.Vector3());
      const center=box.getCenter(new THREE.Vector3());
      const modelScale=(.92/Math.max(size.y,.0001))*(Number(design.scale)||1);
      orientedModel.position.set(
        -center.x*modelScale,
        -box.min.y*modelScale,
        -center.z*modelScale,
      );
      orientedModel.scale.setScalar(modelScale);
      model.traverse((child)=>{
        if(!child.isMesh)return;
        child.castShadow=true;
        child.receiveShadow=true;
      });
      root.clear();
      root.add(orientedModel);
    }).catch(()=>{});
    return root;
  }
  const metalness=Number(design.metalness??.32);
  const roughness=Number(design.roughness??.52);
  const mainMaterial=new THREE.MeshPhysicalMaterial({
    color:design.color||'#6f7180',
    metalness,
    roughness,
    clearcoat:.28,
  });
  const accentMaterial=new THREE.MeshPhysicalMaterial({
    color:design.accent||'#d9dde2',
    metalness:Math.max(.1,metalness-.08),
    roughness:Math.min(.9,roughness+.02),
    clearcoat:.45,
  });
  const baseMaterial=new THREE.MeshStandardMaterial({
    color:design.baseColor||'#34313d',
    metalness:.24,
    roughness:.62,
  });
  const baseWidth=Number(design.baseWidth??.58);
  const baseDepth=Number(design.baseDepth??.34);
  const baseHeight=Number(design.baseHeight??.12);

  if (design.style === 'cone') {
    const base=materialBox(baseWidth,baseHeight,baseDepth,baseMaterial,.04);
    base.position.y=baseHeight*.5;
    const coneHeight=Number(design.coneHeight??.52);
    const cone=new THREE.Mesh(
      new THREE.CylinderGeometry(
        Number(design.coneTopRadius??.055),
        Number(design.coneBottomRadius??.2),
        coneHeight,
        24,
      ),
      mainMaterial,
    );
    cone.position.y=baseHeight+coneHeight*.5;
    const stripeHeight=Number(design.stripeHeight??.1);
    const stripeRadius=Number(design.stripeRadius??.145);
    const stripe=new THREE.Mesh(
      new THREE.CylinderGeometry(stripeRadius*.78,stripeRadius,stripeHeight,24),
      accentMaterial,
    );
    stripe.position.y=baseHeight+Number(design.stripeOffsetY??.29);
    root.add(base,cone,stripe);
  } else if (design.style === 'holo') {
    const base=new THREE.Mesh(
      new THREE.CylinderGeometry(baseWidth*.42,baseWidth*.5,baseHeight,28),
      baseMaterial,
    );
    base.position.y=baseHeight*.5;
    base.scale.z=baseDepth/baseWidth;
    const stemHeight=Number(design.stemHeight??.48);
    const stemRadius=Number(design.stemRadius??.035);
    const stem=new THREE.Mesh(
      new THREE.CylinderGeometry(stemRadius*.72,stemRadius,stemHeight,14),
      accentMaterial,
    );
    stem.position.y=baseHeight+stemHeight*.5;
    const ring=new THREE.Mesh(
      new THREE.TorusGeometry(
        Number(design.ringRadius??.22),
        Number(design.ringThickness??.035),
        12,
        40,
      ),
      new THREE.MeshBasicMaterial({
        color:design.accent||'#55e3ff',
        transparent:true,
        opacity:Number(design.glowOpacity??.8),
      }),
    );
    ring.position.y=baseHeight+Number(design.ringHeight??.42);
    ring.rotation.x=Math.PI/2;
    root.add(base,stem,ring);
  } else if (design.style === 'blocks') {
    const base=materialBox(baseWidth,baseHeight,baseDepth,baseMaterial,.035);
    base.position.y=baseHeight*.5;
    const uprightHeight=Number(design.uprightHeight??.58);
    const upright=materialBox(
      Number(design.uprightWidth??.14),
      uprightHeight,
      Number(design.uprightDepth??.14),
      accentMaterial,
      .018,
    );
    upright.position.y=baseHeight+uprightHeight*.5;
    const cross=materialBox(
      Number(design.crossWidth??.46),
      Number(design.crossHeight??.14),
      Number(design.crossDepth??.14),
      mainMaterial,
      .018,
    );
    cross.position.y=baseHeight+Number(design.crossOffsetY??.48);
    root.add(base,upright,cross);
  } else {
    const base=materialBox(baseWidth,baseHeight,baseDepth,baseMaterial,.045);
    base.position.y=baseHeight*.5;
    const slabHeight=Number(design.slabHeight??.62);
    const slabDepth=Number(design.slabDepth??.15);
    const slab=materialBox(
      Number(design.slabWidth??.48),
      slabHeight,
      slabDepth,
      mainMaterial,
      Number(design.slabRoundness??.13),
    );
    slab.position.y=baseHeight+slabHeight*.5;
    const plateDepth=Number(design.plateDepth??.025);
    const plate=materialBox(
      Number(design.plateWidth??.3),
      Number(design.plateHeight??.16),
      plateDepth,
      accentMaterial,
      .035,
    );
    plate.position.set(
      0,
      baseHeight+Number(design.plateOffsetY??.4),
      -(slabDepth+plateDepth)*.5,
    );
    root.add(base,slab,plate);
  }
  root.scale.setScalar(Number(design.scale)||1);
  root.traverse((child)=>{if(child.isMesh){child.castShadow=true;child.receiveShadow=true;}});
  return root;
}

function projectileHitsObstacle(position,obstacle,margin=.06) {
  if(position.y<obstacle.minY-margin||position.y>obstacle.maxY+margin)return false;
  if(obstacle.shape==='circle'){
    const dx=position.x-obstacle.x;
    const dz=position.z-obstacle.z;
    const radius=obstacle.radius+margin;
    return dx*dx+dz*dz<=radius*radius;
  }
  return position.x>=obstacle.minX-margin&&position.x<=obstacle.maxX+margin
    &&position.z>=obstacle.minZ-margin&&position.z<=obstacle.maxZ+margin;
}

function effectParticleGeometry(style,size){
  if(style==='confetti')return new THREE.BoxGeometry(size*.48,size*1.45,size*.16);
  if(style==='firework')return new THREE.OctahedronGeometry(size*.72,0);
  if(style==='plasma')return new THREE.SphereGeometry(size,8,6);
  return new THREE.IcosahedronGeometry(size,0);
}

function explosionVelocity(design){
  const angle=Math.random()*Math.PI*2;
  const elevation=design.style==='firework'
    ?.12+Math.random()*.72
    :design.style==='confetti'
      ?.25+Math.random()*.72
      :-.12+Math.random()*.82;
  const horizontal=Math.cos(elevation);
  const speed=Number(design.power??5.5)*(.48+Math.random()*.58);
  return new THREE.Vector3(
    Math.cos(angle)*horizontal*speed,
    Math.sin(elevation)*speed,
    Math.sin(angle)*horizontal*speed,
  );
}

export class EffectsSystem {
  constructor(scene) {
    this.scene = scene;
    this.items = [];
    this.markers = [];
    this.boostEmissionIndex = 0;
    this.particleMultiplier = 1;
    this.maxParticles = 240;
  }

  setQuality(quality='high') {
    const profiles={
      low:{multiplier:.34,maxParticles:80},
      medium:{multiplier:.66,maxParticles:150},
      high:{multiplier:1,maxParticles:240},
    };
    const profile=profiles[quality]||profiles.high;
    this.particleMultiplier=profile.multiplier;
    this.maxParticles=profile.maxParticles;
    while(this.items.length>this.maxParticles){
      const item=this.items.shift();
      this.scene.remove(item.mesh);
      item.mesh.geometry?.dispose();
      if(!item.mesh.material?.userData?.cached)item.mesh.material?.dispose();
    }
  }

  burst(position, color = '#ffd64d', count = 8, power = 5) {
    const qualityCount=Math.max(1,Math.round(count*this.particleMultiplier));
    for (let i = 0; i < qualityCount; i += 1) {
      if(this.items.length>=this.maxParticles)break;
      const size = .06 + Math.random() * .12;
      const mesh = sphere(size, color, 7, 5);
      mesh.material = new THREE.MeshBasicMaterial({ color, transparent: true });
      mesh.position.copy(position);
      mesh.position.y += .3;
      this.scene.add(mesh);
      this.items.push({
        mesh,
        velocity: new THREE.Vector3((Math.random()-.5)*power, Math.random()*power*.65, (Math.random()-.5)*power),
        life: .35 + Math.random()*.35,
        maxLife: .7,
        gravity: 8,
      });
    }
  }

  dust(position, velocity, color = '#d9bd89') {
    if(this.items.length>=this.maxParticles||Math.random()>this.particleMultiplier)return;
    const mesh = sphere(.1 + Math.random()*.16, color, 7, 5);
    mesh.material = new THREE.MeshBasicMaterial({ color, transparent: true, opacity: .44, depthWrite: false });
    mesh.position.copy(position);
    mesh.position.y += .13;
    this.scene.add(mesh);
    this.items.push({
      mesh,
      velocity: velocity.clone().multiplyScalar(-.05).add(new THREE.Vector3((Math.random()-.5)*.8,.5+Math.random()*.6,(Math.random()-.5)*.8)),
      life: .5,
      maxLife: .5,
      gravity: -.2,
      grow: true,
    });
  }

  vehicleBoostSpark(car) {
    const visual=car?.userData?.visual;
    const socket=car?.userData?.nitroSocket;
    const design=car?.userData?.nitroEffect;
    if(!visual||!socket||!design)return;
    const nozzleCount=Math.max(1,Math.round(Number(design.nozzleCount)||1));
    const nozzleGap=Number(design.nozzleGap)||0;
    const nozzleIndex=this.boostEmissionIndex%nozzleCount;
    this.boostEmissionIndex+=1;
    const nozzleOffset=(nozzleIndex-(nozzleCount-1)*.5)*nozzleGap;
    nitroEmissionPosition.copy(socket);
    nitroEmissionPosition.x+=nozzleOffset;
    visual.localToWorld(nitroEmissionPosition);
    visual.getWorldQuaternion(nitroEmissionQuaternion);
    visual.getWorldScale(nitroEmissionScale);
    const effectScale=Math.max(
      .01,
      (Math.abs(nitroEmissionScale.x)+Math.abs(nitroEmissionScale.y)+Math.abs(nitroEmissionScale.z))/3,
    );
    nitroEmissionDirection
      .set(0,0,-1)
      .applyQuaternion(nitroEmissionQuaternion)
      .normalize();
    this.boostSpark(
      nitroEmissionPosition,
      nitroEmissionDirection,
      design,
      {effectScale,orientation:nitroEmissionQuaternion},
    );
  }

  boostSpark(position, direction, design = null, {effectScale=1,orientation=null} = {}) {
    if(this.items.length>=this.maxParticles||Math.random()>this.particleMultiplier)return;
    const palette = design ? [design.color,design.accent,design.spark] : ['#5feaff','#ffe36a'];
    const color = palette[Math.floor(Math.random()*palette.length)];
    const scale=Math.max(.01,Number(effectScale)||1);
    const size=Number(design?.particleSize??.06)*scale;
    const geometry=effectParticleGeometry(design?.style||'plasma',size);
    const mesh=new THREE.Mesh(geometry,new THREE.MeshBasicMaterial({
      color,
      transparent:true,
      opacity:.9,
      depthWrite:false,
      blending:design?.style==='confetti'?THREE.NormalBlending:THREE.AdditiveBlending,
    }));
    const spread=Number(design?.spread??.34)*scale;
    nitroEmissionJitter.set(
      (Math.random()-.5)*spread*.18,
      (Math.random()-.5)*spread*.18,
      (Math.random()-.5)*spread*.06,
    );
    if(orientation)nitroEmissionJitter.applyQuaternion(orientation);
    mesh.position.copy(position).add(nitroEmissionJitter);
    mesh.rotation.set(Math.random()*Math.PI,Math.random()*Math.PI,Math.random()*Math.PI);
    this.scene.add(mesh);
    const speed=Number(design?.speed??3)*scale*(.78+Math.random()*.44);
    const life=Math.max(
      .08,
      Number(design?.trailLength??1.55)*scale/Math.max(.1*scale,speed),
    );
    nitroEmissionDrift.set(
      (Math.random()-.5)*spread*.75,
      (Math.random()-.5)*spread*.6,
      0,
    );
    if(orientation)nitroEmissionDrift.applyQuaternion(orientation);
    this.items.push({
      mesh,
      velocity:direction.clone().multiplyScalar(-speed).add(nitroEmissionDrift),
      life,
      maxLife:life,
      gravity:0,
      spin:design?.style==='confetti'
        ?new THREE.Vector3(4+Math.random()*3,0,6+Math.random()*3)
        :null,
    });
  }

  projectileSmoke(position, accent='#a5a2b0', scale=1) {
    if(this.items.length>=this.maxParticles||Math.random()>this.particleMultiplier)return;
    const color=Math.random()<.22?accent:'#777582';
    const size=(.045+Math.random()*.065)*scale;
    const mesh=sphere(size,color,6,4);
    mesh.material=new THREE.MeshBasicMaterial({color,transparent:true,opacity:.34,depthWrite:false});
    mesh.position.copy(position).add(new THREE.Vector3((Math.random()-.5)*.07,(Math.random()-.5)*.07,(Math.random()-.5)*.07));
    this.scene.add(mesh);
    const life=.32+Math.random()*.18;
    this.items.push({
      mesh,
      velocity:new THREE.Vector3((Math.random()-.5)*.15,.08+Math.random()*.12,(Math.random()-.5)*.15),
      life,
      maxLife:life,
      gravity:-.05,
      grow:true,
    });
  }

  elimination(position, design) {
    const duration=Number(design.duration??.95);
    const origin=position.clone();
    origin.y+=.32;
    const particleCount=Math.max(
      1,
      Math.round(Number(design.particleCount??30)*this.particleMultiplier),
    );
    const particleSize=Number(design.particleSize??.11);
    for(let index=0;index<particleCount;index+=1){
      if(this.items.length>=this.maxParticles)break;
      const color=index%2?(design.color||'#ffd83d'):(design.accent||'#ff742f');
      const mesh=new THREE.Mesh(
        effectParticleGeometry(design.style,particleSize),
        new THREE.MeshBasicMaterial({
          color,
          transparent:true,
          opacity:1,
          depthWrite:false,
          blending:design.style==='confetti'?THREE.NormalBlending:THREE.AdditiveBlending,
        }),
      );
      mesh.position.copy(origin);
      mesh.scale.setScalar(.55+Math.random()*.7);
      mesh.rotation.set(Math.random()*Math.PI,Math.random()*Math.PI,Math.random()*Math.PI);
      this.scene.add(mesh);
      this.items.push({
        mesh,
        velocity:explosionVelocity(design),
        life:duration,
        maxLife:duration,
        gravity:Number(design.gravity??3),
        spin:new THREE.Vector3(
          (Math.random()-.5)*12,
          (Math.random()-.5)*12,
          (Math.random()-.5)*12,
        ),
        grow:design.style==='plasma',
      });
    }

    const flashSize=Number(design.flashSize??.5);
    const flash = sphere(1,design.accent||'#ff742f',18,12);
    flash.material = new THREE.MeshBasicMaterial({
      color:design.accent||'#ff742f',
      transparent:true,
      opacity:.76,
      depthWrite:false,
      blending:THREE.AdditiveBlending,
    });
    flash.position.copy(origin);
    flash.scale.setScalar(flashSize);
    this.scene.add(flash);
    this.items.push({
      mesh:flash,
      velocity:new THREE.Vector3(),
      life:duration,
      maxLife:duration,
      gravity:0,
      initialScale:flashSize,
      targetScale:flashSize*3.2,
      fadeMultiplier:3,
      baseOpacity:.76,
    });

    const shockwaveThickness=Number(design.shockwaveThickness??.05);
    const ring=new THREE.Mesh(
      new THREE.TorusGeometry(.5,shockwaveThickness,10,48),
      new THREE.MeshBasicMaterial({
        color:design.color||'#ffd83d',
        transparent:true,
        opacity:.72,
        depthWrite:false,
        blending:THREE.AdditiveBlending,
      }),
    );
    ring.position.copy(origin);
    ring.rotation.x=Math.PI/2;
    ring.scale.setScalar(.08);
    this.scene.add(ring);
    this.items.push({
      mesh:ring,
      velocity:new THREE.Vector3(),
      life:duration,
      maxLife:duration,
      gravity:0,
      initialScale:.08,
      targetScale:Number(design.shockwaveSize??1.4)/.5,
      easeScale:true,
      fadeMultiplier:1,
      baseOpacity:.72,
    });
  }

  shockwave(position,{color='#cf8cff',shockwaveColor=color,shockwaveSize=5.7,duration=.55}={}){
    const palette=[shockwaveColor,'#65efff','#fff49a'];
    const ringSettings=[
      {delayScale:.05,target:shockwaveSize/.5,opacity:.92,thickness:.075,life:duration},
      {delayScale:.15,target:shockwaveSize*.88/.5,opacity:.7,thickness:.035,life:duration*.88},
      {delayScale:.28,target:shockwaveSize*.66/.5,opacity:.58,thickness:.025,life:duration*.72},
    ];
    ringSettings.forEach((settings,index)=>{
      const ring=new THREE.Mesh(
        new THREE.TorusGeometry(.5,settings.thickness,10,64),
        new THREE.MeshBasicMaterial({
          color:palette[index],transparent:true,opacity:settings.opacity,depthWrite:false,
          blending:THREE.AdditiveBlending,
        }),
      );
      ring.position.copy(position);
      ring.position.y+=index*.035;
      ring.rotation.x=Math.PI/2;
      ring.scale.setScalar(settings.delayScale);
      this.scene.add(ring);
      this.items.push({mesh:ring,velocity:new THREE.Vector3(),life:settings.life,maxLife:settings.life,
        gravity:0,initialScale:settings.delayScale,targetScale:settings.target,easeScale:true,
        fadeMultiplier:1,baseOpacity:settings.opacity});
    });

    const dome=new THREE.Mesh(
      new THREE.SphereGeometry(.5,28,16,0,Math.PI*2,0,Math.PI*.53),
      new THREE.MeshBasicMaterial({
        color:'#b980ff',wireframe:true,transparent:true,opacity:.34,depthWrite:false,
        blending:THREE.AdditiveBlending,side:THREE.DoubleSide,
      }),
    );
    dome.position.copy(position);
    dome.scale.setScalar(.15);
    this.scene.add(dome);
    this.items.push({mesh:dome,velocity:new THREE.Vector3(),life:duration*.82,maxLife:duration*.82,
      gravity:0,initialScale:.15,targetScale:shockwaveSize*1.55,easeScale:true,
      fadeMultiplier:1,baseOpacity:.34});

    const core=sphere(.42,'#ffffff',18,12);
    core.material=new THREE.MeshBasicMaterial({
      color:'#ffffff',transparent:true,opacity:.92,depthWrite:false,blending:THREE.AdditiveBlending,
    });
    core.position.copy(position).add(new THREE.Vector3(0,.22,0));
    this.scene.add(core);
    this.items.push({mesh:core,velocity:new THREE.Vector3(),life:.24,maxLife:.24,gravity:0,
      initialScale:.4,targetScale:5.4,easeScale:true,fadeMultiplier:1.25,baseOpacity:.92});

    const rayCount=Math.max(10,Math.round(28*this.particleMultiplier));
    for(let index=0;index<rayCount&&this.items.length<this.maxParticles;index+=1){
      const angle=index/rayCount*Math.PI*2+(Math.random()-.5)*.12;
      const shard=new THREE.Mesh(
        new THREE.ConeGeometry(.045,.5+Math.random()*.65,5),
        new THREE.MeshBasicMaterial({
          color:palette[index%palette.length],transparent:true,opacity:.94,depthWrite:false,
          blending:THREE.AdditiveBlending,
        }),
      );
      shard.position.copy(position).add(new THREE.Vector3(Math.cos(angle)*.28,.16,Math.sin(angle)*.28));
      shard.rotation.set(Math.PI/2,0,-angle-Math.PI/2);
      this.scene.add(shard);
      const speed=6+Math.random()*6;
      const life=.36+Math.random()*.28;
      this.items.push({mesh:shard,velocity:new THREE.Vector3(
        Math.cos(angle)*speed,.7+Math.random()*2.1,Math.sin(angle)*speed,
      ),life,maxLife:life,gravity:5.5,spin:new THREE.Vector3(0,4,2),baseOpacity:.94});
    }
  }

  powerupCrateAppear(position){
    const origin=position.clone();
    const beamHeight=7.5;
    const beam=new THREE.Mesh(
      new THREE.CylinderGeometry(.05,.18,beamHeight,12,1,true),
      new THREE.MeshBasicMaterial({
        color:'#71efff',transparent:true,opacity:.58,depthWrite:false,
        blending:THREE.AdditiveBlending,side:THREE.DoubleSide,
      }),
    );
    beam.position.copy(origin);beam.position.y+=beamHeight*.5;
    this.scene.add(beam);
    this.items.push({mesh:beam,velocity:new THREE.Vector3(),life:.56,maxLife:.56,gravity:0,
      fadeMultiplier:1,baseOpacity:.58});

    const points=[];
    for(let index=0;index<9;index+=1){
      const progress=index/8;
      points.push(new THREE.Vector3(
        origin.x+(index===0||index===8?0:(Math.random()-.5)*.34),
        origin.y+beamHeight*(1-progress),
        origin.z+(index===0||index===8?0:(Math.random()-.5)*.34),
      ));
    }
    const lightning=new THREE.Line(
      new THREE.BufferGeometry().setFromPoints(points),
      new THREE.LineBasicMaterial({color:'#fff7bd',transparent:true,opacity:1,blending:THREE.AdditiveBlending}),
    );
    this.scene.add(lightning);
    this.items.push({mesh:lightning,velocity:new THREE.Vector3(),life:.34,maxLife:.34,gravity:0,
      fadeMultiplier:1,baseOpacity:1});
    this.powerupPickup(position,{count:18,power:5.5,ringSize:2.1});
  }

  powerupCrateOpen(position){
    const origin=position.clone();
    const fragmentCount=Math.max(6,Math.round(12*this.particleMultiplier));
    const colors=['#f6bf35','#6cefff','#743eb5'];
    for(let index=0;index<fragmentCount&&this.items.length<this.maxParticles;index+=1){
      const angle=index/fragmentCount*Math.PI*2+(Math.random()-.5)*.3;
      const golden=index%3===0;
      const fragment=new THREE.Mesh(
        new RoundedBoxGeometry(
          golden ? .08 : .15+Math.random()*.1,
          .07+Math.random()*.08,
          golden ? .28 : .1+Math.random()*.08,
          2,
          .018,
        ),
        new THREE.MeshBasicMaterial({
          color:colors[index%colors.length],transparent:true,opacity:1,depthWrite:false,
          blending:golden?THREE.AdditiveBlending:THREE.NormalBlending,
        }),
      );
      fragment.position.copy(origin).add(new THREE.Vector3(
        Math.cos(angle)*.24,(Math.random()-.25)*.4,Math.sin(angle)*.24,
      ));
      fragment.rotation.set(Math.random()*Math.PI,Math.random()*Math.PI,Math.random()*Math.PI);
      this.scene.add(fragment);
      const speed=3.2+Math.random()*4.4;
      const life=.52+Math.random()*.32;
      this.items.push({mesh:fragment,velocity:new THREE.Vector3(
        Math.cos(angle)*speed,2.2+Math.random()*3.8,Math.sin(angle)*speed,
      ),life,maxLife:life,gravity:9,spin:new THREE.Vector3(
        (Math.random()-.5)*13,(Math.random()-.5)*13,(Math.random()-.5)*13,
      )});
    }
    this.powerupPickup(position,{count:34,power:8.2,ringSize:3});
  }

  powerupPickup(position,{count=28,power=7,ringSize=2.6}={}){
    this.burst(position,'#fff16a',count,power);
    this.burst(position,'#7cefff',Math.round(count*.7),power*.8);
    ['#fff16a','#9b65ff'].forEach((color,index)=>{
      const ring=new THREE.Mesh(
        new THREE.TorusGeometry(.42,index ? .028 : .052,8,42),
        new THREE.MeshBasicMaterial({
          color,transparent:true,opacity:index ? .62 : .9,depthWrite:false,
          blending:THREE.AdditiveBlending,
        }),
      );
      ring.position.copy(position);ring.position.y-=.38-index*.04;ring.rotation.x=Math.PI/2;
      ring.scale.setScalar(.12+index*.08);
      this.scene.add(ring);
      const life=.42+index*.12;
      this.items.push({mesh:ring,velocity:new THREE.Vector3(),life,maxLife:life,gravity:0,
        initialScale:ring.scale.x,targetScale:ringSize/(.42),easeScale:true,
        fadeMultiplier:1,baseOpacity:index ? .62 : .9});
    });
  }

  placeTombstone(position, design, paintFinish = null) {
    const marker = createTombstoneMarker(design,paintFinish);
    marker.position.copy(position);
    marker.position.y += .02;
    marker.rotation.y = Math.random()*Math.PI*2;
    this.scene.add(marker);
    this.markers.push(marker);
    if (this.markers.length > 24) this.scene.remove(this.markers.shift());
    return marker;
  }

  update(dt) {
    for (let i = this.items.length - 1; i >= 0; i -= 1) {
      const item = this.items[i];
      item.life -= dt;
      if (item.life <= 0) {
        this.scene.remove(item.mesh);
        item.mesh.geometry?.dispose();
        if (!item.mesh.material.userData?.cached) item.mesh.material?.dispose();
        this.items.splice(i, 1);
        continue;
      }
      item.velocity.y -= item.gravity * dt;
      item.mesh.position.addScaledVector(item.velocity, dt);
      const ratio = item.life / item.maxLife;
      const elapsed=1-ratio;
      item.mesh.material.opacity=item.fadeMultiplier
        ?Number(item.baseOpacity??1)*Math.max(0,1-elapsed*item.fadeMultiplier)
        :Math.min(item.mesh.material.opacity,ratio);
      if(item.spin){
        item.mesh.rotation.x+=item.spin.x*dt;
        item.mesh.rotation.y+=item.spin.y*dt;
        item.mesh.rotation.z+=item.spin.z*dt;
      }
      if(Number.isFinite(item.targetScale)){
        const progress=item.easeScale?1-Math.pow(1-elapsed,2):elapsed;
        item.mesh.scale.setScalar(THREE.MathUtils.lerp(item.initialScale,item.targetScale,progress));
      }
      if (item.grow) item.mesh.scale.multiplyScalar(1 + dt * 1.8);
    }
  }

  clear() {
    this.items.forEach((item) => this.scene.remove(item.mesh));
    this.items.length = 0;
    this.markers.forEach((marker)=>this.scene.remove(marker));
    this.markers.length = 0;
  }
}

export class ProjectileSystem {
  constructor(scene, effects, onImpact = null, onNetworkSpawn = null) {
    this.scene = scene;
    this.effects = effects;
    this.onImpact = onImpact || (()=>{});
    this.onNetworkSpawn = onNetworkSpawn || (()=>{});
    this.projectiles = [];
    this.deferredClear = new DeferredClear(()=>this.clearNow());
  }

  fire(vehicle, aimDirection = null, options={}) {
    const activeCount=this.projectiles.reduce(
      (count,projectile)=>count+(projectile.owner===vehicle?1:0),
      0,
    );
    if(!canFireProjectile(vehicle,activeCount))return false;
    const design=getCannon(vehicle.car.userData.cannonId);
    const projectileDesign=getProjectile(vehicle.car.userData.projectileId||design.defaultProjectileId);
    const root=createProjectileModel(projectileDesign.id,vehicle.car.userData.paintFinish);
    const modelScale = vehicle.car.userData.modelScale;
    root.scale.multiplyScalar(modelScale);
    const cannon=vehicle.car.userData.cannon;
    const muzzleZ=Number(cannon.userData.muzzleZ??-.79);
    const muzzleY=Number(cannon.userData.muzzleY??.03);
    const muzzle=cannon.localToWorld(new THREE.Vector3(0,muzzleY,muzzleZ));
    const launch=projectileLaunchState(
      vehicle,
      STANDARD_CANNON_PERFORMANCE.projectileRadius,
      aimDirection,
    );
    const visualOffset=muzzle.clone().sub(launch.position);
    root.position.copy(muzzle);
    root.quaternion.setFromUnitVectors(new THREE.Vector3(0,0,-1), launch.forward);
    this.scene.add(root);
    this.effects.burst(muzzle,design.accent,3,1.25);
    this.projectiles.push({
      root,
      position:launch.position,
      visualOffset,
      velocity:launch.forward.multiplyScalar(STANDARD_CANNON_PERFORMANCE.projectileSpeed).addScaledVector(vehicle.velocity,.45),
      owner:vehicle,
      radius:STANDARD_CANNON_PERFORMANCE.projectileRadius,
      life:STANDARD_CANNON_PERFORMANCE.projectileLife,
      smokeClock:0,
      projectileId:projectileDesign.id,
      smokeColor:projectileDesign.accent,
      launchGrounded:launch.launchGrounded,
      launchSurfaceY:launch.launchSurfaceY,
      homingTarget:options.homingTarget||null,
      homingTargetId:options.homingTargetId||options.homingTarget?.networkId||null,
      guided:Boolean(options.homingTarget||options.homingTargetId),
    });
    consumeProjectileFire(vehicle);
    return true;
  }

  firePredicted(vehicle,predictedSeq,ownerId,aimDirection=null,options={}) {
    if(!Number.isSafeInteger(predictedSeq)||!this.fire(vehicle,aimDirection,options))return false;
    const projectile=this.projectiles[this.projectiles.length-1];
    projectile.predictedSeq=predictedSeq;
    projectile.predictedOwnerId=ownerId;
    projectile.localPredicted=true;
    return true;
  }

  removePredicted(ownerId,sourceSeq) {
    if(!Number.isSafeInteger(sourceSeq))return false;
    const index=this.projectiles.findIndex((projectile)=>
      projectile.localPredicted
      &&projectile.predictedOwnerId===ownerId
      &&projectile.predictedSeq===sourceSeq
    );
    if(index<0)return false;
    this.scene.remove(this.projectiles[index].root);
    this.projectiles.splice(index,1);
    return true;
  }

  syncNetwork(states, resolveOwner, {localOwnerId=null,localAck=-1}={}) {
    const activeIds=new Set(states.map((state)=>state.id));
    const activeLocalSequences=new Set();
    states.forEach((state)=>{
      const sourceSeq=Number.isSafeInteger(state.sourceSeq)?state.sourceSeq:null;
      if(state.ownerId===localOwnerId&&Number.isSafeInteger(sourceSeq))activeLocalSequences.add(sourceSeq);
      let dart=this.projectiles.find((projectile)=>projectile.networkId===state.id);
      if(!dart&&state.ownerId===localOwnerId&&Number.isSafeInteger(sourceSeq)){
        dart=this.projectiles.find((projectile)=>
          projectile.localPredicted
          &&!projectile.networkId
          &&projectile.predictedOwnerId===localOwnerId
          &&projectile.predictedSeq===sourceSeq
        );
        if(dart)dart.networkId=state.id;
      }
      if(!dart){
        const owner=resolveOwner(state.ownerId);
        const projectileId=state.projectileId||getCannon(state.cannonId).defaultProjectileId;
        const root=createProjectileModel(
          projectileId,
          owner?.car?.userData?.paintFinish,
        );
        root.scale.multiplyScalar(owner?.car?.userData?.modelScale||1);
        root.position.set(state.x,state.y,state.z);
        this.scene.add(root);
        dart={
          root,
          position:new THREE.Vector3(state.x,state.y,state.z),
          visualOffset:new THREE.Vector3(),
          networkId:state.id,
          velocity:new THREE.Vector3(state.vx,state.vy,state.vz),
          owner,
          radius:state.radius,
          life:state.life,
          smokeClock:0,
          projectileId,
          smokeColor:root.userData.smokeColor,
          launchGrounded:Boolean(state.launchGrounded),
          launchSurfaceY:Number(state.launchSurfaceY)||0,
          homingTargetId:state.homingTargetId||null,
          homingTarget:state.homingTargetId?resolveOwner(state.homingTargetId):null,
          guided:Boolean(state.guided),
        };
        this.projectiles.push(dart);
        this.onNetworkSpawn({
          position:root.position.clone(),
          owner,
          ownerId:state.ownerId,
          cannonId:state.cannonId,
          projectileId,
        });
      }else{
        const authoritative=new THREE.Vector3(state.x,state.y,state.z);
        if(dart.localPredicted){
          const authoritativeVelocity=new THREE.Vector3(state.vx,state.vy,state.vz);
          const correction=authoritative.sub(dart.position);
          if(correction.lengthSq()>25)dart.position.set(state.x,state.y,state.z);
          else if(authoritativeVelocity.lengthSq()>.0001){
            const direction=authoritativeVelocity.normalize();
            const forwardCorrection=correction.dot(direction);
            if(forwardCorrection>0)dart.position.addScaledVector(direction,Math.min(forwardCorrection,.35));
            correction.addScaledVector(direction,-forwardCorrection);
            dart.position.addScaledVector(correction,.16);
          }
        }else if(dart.position.distanceToSquared(authoritative)>.64)dart.position.copy(authoritative);
        else dart.position.lerp(authoritative,.58);
        dart.velocity.set(state.vx,state.vy,state.vz);
        dart.life=state.life;
        dart.launchGrounded=Boolean(state.launchGrounded);
        dart.launchSurfaceY=Number(state.launchSurfaceY)||0;
        dart.homingTargetId=state.homingTargetId||null;
        dart.homingTarget=state.homingTargetId?resolveOwner(state.homingTargetId):null;
        dart.guided=Boolean(state.guided);
      }
      dart.root.position.copy(dart.position).add(dart.visualOffset);
      if(dart.velocity.lengthSq()>.0001){
        dart.root.quaternion.setFromUnitVectors(
          new THREE.Vector3(0,0,-1),
          dart.velocity.clone().normalize(),
        );
      }
    });
    for(let index=this.projectiles.length-1;index>=0;index-=1){
      const dart=this.projectiles[index];
      const confirmedMissing=dart.networkId&&!activeIds.has(dart.networkId);
      const predictionRejected=dart.localPredicted
        &&!dart.networkId
        &&Number.isSafeInteger(localAck)
        &&dart.predictedSeq<=localAck
        &&!activeLocalSequences.has(dart.predictedSeq);
      if(!confirmedMissing&&!predictionRejected)continue;
      this.scene.remove(dart.root);
      this.projectiles.splice(index,1);
    }
  }

  updateNetwork(dt) {
    for(let index=this.projectiles.length-1;index>=0;index-=1){
      const dart=this.projectiles[index];
      if(!dart.networkId&&!dart.localPredicted)continue;
      dart.life-=dt;
      if(dart.guided&&dart.homingTarget&&!dart.homingTarget.disabled){
        const targetPoint=dart.homingTarget.car.position.clone();
        targetPoint.y+=(dart.homingTarget.car.userData.collisionHeight||.82)*.5;
        steerHomingVelocity(dart.velocity,dart.position,targetPoint,dt);
      }
      dart.position.addScaledVector(dart.velocity,dt);
      convergeProjectileVisualOffset(dart.visualOffset,dt);
      dart.root.position.copy(dart.position).add(dart.visualOffset);
      dart.root.rotateZ(dt*(dart.root.userData.spin||8));
      dart.smokeClock-=dt;
      if(dart.smokeClock<=0){
        dart.smokeClock=.038+Math.random()*.025;
        this.effects.projectileSmoke(
          dart.root.position.clone().addScaledVector(dart.velocity,-.009),
          dart.smokeColor,
        );
      }
      if((dart.localPredicted&&!dart.networkId&&dart.life<=0)||dart.life<-.3){
        this.scene.remove(dart.root);
        this.projectiles.splice(index,1);
      }
    }
  }

  update(dt, vehicles, world, onVehicleHit=null) {
    this.deferredClear.beginUpdate();
    try {
      for (let i=this.projectiles.length-1;i>=0;i-=1){
        if(this.deferredClear.pending)break;
        const dart=this.projectiles[i];
        if(!dart)continue;
        const start=dart.position.clone();
        dart.life-=dt;
        if(dart.guided&&dart.homingTarget&&!dart.homingTarget.disabled){
          const targetPoint=dart.homingTarget.car.position.clone();
          targetPoint.y+=(dart.homingTarget.car.userData.collisionHeight||.82)*.5;
          steerHomingVelocity(dart.velocity,dart.position,targetPoint,dt);
        }
        dart.position.addScaledVector(dart.velocity,dt);
        convergeProjectileVisualOffset(dart.visualOffset,dt);
        dart.root.position.copy(dart.position).add(dart.visualOffset);
        dart.root.rotateZ(dt*(dart.root.userData.spin||8));
        dart.smokeClock-=dt;
        if(dart.smokeClock<=0){
          dart.smokeClock=.038+Math.random()*.025;
          const smokePosition=dart.root.position.clone().addScaledVector(dart.velocity,-.009);
          this.effects.projectileSmoke(smokePosition,dart.smokeColor);
        }
        let hit=false;
        const vehicleHits=[];
        for(const vehicle of vehicles){
          if(vehicle===dart.owner||vehicle.disabled) continue;
          if(dart.owner.teamId&&vehicle.teamId===dart.owner.teamId)continue;
          const intersection=projectileVehicleIntersection(start,dart.position,vehicle,dart.radius,{
            launchGrounded:dart.launchGrounded,
            launchSurfaceY:dart.launchSurfaceY,
          });
          if(intersection)vehicleHits.push({vehicle,intersection});
        }
        vehicleHits.sort((first,second)=>first.intersection.time-second.intersection.time);
        const direction=dart.velocity.clone().normalize();
        for(const {vehicle,intersection} of vehicleHits){
          const shielded=vehicle.powerupShieldTimer>0;
          const response=shielded?{absorbed:true,handled:true}:onVehicleHit?.({
            target:vehicle,
            owner:dart.owner,
            direction,
            position:intersection.position.clone(),
            projectile:dart,
            projectileId:dart.projectileId,
          });
          const ignored=response==='ignore'||response?.ignore===true;
          const handled=response==='handled'||response?.handled===true;
          if(!ignored){
            const absorbed=shielded||response?.absorbed===true;
            const responseStrength=Number(response?.impactStrength);
            const impactStrength=absorbed?0:Number.isFinite(responseStrength)
              ?responseStrength:(handled?.72:1);
            if(!absorbed)vehicle.applyProjectileImpact(
              direction,
              intersection.position,
              impactStrength,
            );
            if(!handled&&!absorbed){
              const damage=(GAME_MODE_RULES[dart.owner.gameMode]||GAME_MODE_RULES.practice).projectileDamage;
              vehicle.damage(damage,direction,dart.owner);
            }
            dart.position.copy(intersection.position);
            dart.root.position.copy(intersection.position);
            this.effects.burst(intersection.position,absorbed?'#68f1ff':'#ffdf55',absorbed?12:9,absorbed?5.2:4.5);
            this.onImpact(intersection.position,vehicle,dart.owner,dart.projectileId,{absorbed});
            hit=true;
            break;
          }
        }
        if(!hit){
          const outside=Math.abs(dart.position.x)>world.width/2||Math.abs(dart.position.z)>world.depth/2||dart.position.y<0;
          const obstacleHit=!outside&&world.obstacles.some((obstacle)=>projectileHitsObstacle(dart.position,obstacle,dart.radius));
          if(outside||obstacleHit){
            this.effects.burst(dart.position,'#ffd9a0',5,2.8);
            this.onImpact(dart.position,null,dart.owner,dart.projectileId);
            hit=true;
          }
        }
        if(hit||dart.life<=0){this.scene.remove(dart.root);this.projectiles.splice(i,1);}
      }
    } finally {
      this.deferredClear.endUpdate();
    }
  }

  clearNow(){
    this.projectiles.forEach((dart)=>this.scene.remove(dart.root));
    this.projectiles.length=0;
  }

  clear(){this.deferredClear.request();}
}

export function createProjectileModel(projectileId,paintFinish=null){
  const design=getProjectile(projectileId);
  const root=new THREE.Group();
  root.name=`projectile-${design.id}`;
  root.userData.spin=Number(design.spin)||8;
  root.userData.smokeColor=design.accent;

  if(design.assetUrl){
    const placeholder=sphere(.12,design.accent,10,7);
    placeholder.scale.z=1.4;
    root.add(placeholder);
    let promise=cosmeticAssetPromises.get(design.assetUrl);
    if(!promise){
      promise=cosmeticGlbLoader.loadAsync(design.assetUrl);
      cosmeticAssetPromises.set(design.assetUrl,promise);
    }
    promise.then((gltf)=>{
      const model=gltf.scene.clone(true);
      prepareVehicleFinishMaterials(model);
      if(paintFinish)applyVehicleBodyFinish(model,paintFinish);
      const box=new THREE.Box3().setFromObject(model);
      const size=box.getSize(new THREE.Vector3());
      const center=box.getCenter(new THREE.Vector3());
      const maxDimension=Math.max(size.x,size.y,size.z)||1;
      const modelScale=(.72/maxDimension)*(Number(design.scale)||1);
      model.position.copy(center).multiplyScalar(-modelScale);
      model.scale.setScalar(modelScale);
      model.traverse((child)=>{
        if(!child.isMesh)return;
        child.castShadow=true;
        child.receiveShadow=true;
      });
      const orientedModel=new THREE.Group();
      orientedModel.rotation.set(
        THREE.MathUtils.degToRad(Number(design.modelRotationX)||0),
        THREE.MathUtils.degToRad(Number(design.modelRotationY)||0),
        THREE.MathUtils.degToRad(Number(design.modelRotationZ)||0),
      );
      orientedModel.add(model);
      root.clear();
      root.add(orientedModel);
    }).catch(()=>{});
    return root;
  }

  root.scale.setScalar(Number(design.scale)||1);
  const main=new THREE.MeshPhysicalMaterial({
    color:design.color||'#d9dde2',
    metalness:.45,
    roughness:.3,
    clearcoat:.5,
  });
  const accent=design.style==='bubble'
    ?new THREE.MeshBasicMaterial({color:design.accent||'#ffcf43'})
    :new THREE.MeshPhysicalMaterial({
      color:design.accent||'#ffcf43',
      metalness:.45,
      roughness:.3,
      clearcoat:.5,
    });
  const projectileSphere=(radius,projectileMaterial)=>
    new THREE.Mesh(new THREE.SphereGeometry(radius,20,12),projectileMaterial);
  const projectileCylinder=(radius,length,projectileMaterial)=>{
    const mesh=new THREE.Mesh(
      new THREE.CylinderGeometry(radius*.8,radius,length,16),
      projectileMaterial,
    );
    mesh.rotation.x=Math.PI/2;
    return mesh;
  };
  if(design.style==='twin'){
    [-.09,.09].forEach((x)=>{
      const tip=projectileSphere(.075,accent);
      tip.scale.z=1.4;
      tip.position.set(x,0,-.1);
      const stem=projectileCylinder(.04,.42,main);
      stem.position.set(x,0,.16);
      root.add(tip,stem);
    });
  }else if(design.style==='bubble'){
    const orb=projectileSphere(.18,accent);
    const core=projectileSphere(.09,main);
    const ring=new THREE.Mesh(
      new THREE.TorusGeometry(.22,.025,8,32),
      main,
    );
    root.add(orb,core,ring);
  }else if(design.style==='rail'){
    const spike=new THREE.Mesh(
      new THREE.ConeGeometry(.12,.78,8),
      accent,
    );
    spike.rotation.x=-Math.PI/2;
    spike.position.z=.05;
    const core=new THREE.Mesh(new THREE.BoxGeometry(.09,.09,.48),main);
    core.position.z=.3;
    root.add(spike,core);
  }else{
    const tip=projectileSphere(.14,accent);
    tip.scale.z=1.4;
    tip.position.z=-.08;
    const stem=projectileCylinder(.055,.55,main);
    stem.position.z=.22;
    root.add(tip,stem);
  }
  root.traverse((child)=>{if(child.isMesh){child.castShadow=true;child.receiveShadow=true;}});
  return root;
}
